import { Navigate, Route, Routes } from "react-router-dom";
import { Layout } from "../layout/Layout";

// === Core domains (Structured) ===
import { AgentsPage } from "../agents/AgentsPage";
import { MemoryPage } from "../memory/MemoryPage";
import { SkillsPage } from "../skills/SkillsPage";
import { SkillDetailPage } from "../skills/SkillDetailPage";
import { MeetingsPage } from "../pages/MeetingsPage";
import { MeetingDetailPage } from "../pages/MeetingDetailPage";
import { UsersPage } from "../users/UsersPage";
import { UserDetailPage } from "../users/UserDetailPage";
import { HomePage } from "../pages/HomePage";

// === Standards + chrome ===
import { KbBlueprintPage } from "../blueprint/KbBlueprintPage";
import { PitchDocBlueprintPage } from "../blueprint/PitchDocBlueprintPage";
import { KnowledgeBaseListPage } from "../knowledge-base-list/KnowledgeBaseListPage";
import { KnowledgeFeedListPage } from "../knowledge-feed/KnowledgeFeedListPage";
import { KnowledgeFeedDetailPage } from "../knowledge-feed/KnowledgeFeedDetailPage";
import { AvatarsPage } from "../avatars/AvatarsPage";
import { SettingsPage } from "../pages/SettingsPage";
import { ContentSwipeFilePage } from "../content-swipe-file/ContentSwipeFilePage";
import { ContentPlanPage } from "../content-plan/ContentPlanPage";
import { ContentMapPage } from "../content-map/ContentMapPage";
import { ThreeShowcasePage } from "../threejs-showcase/ThreeShowcasePage";
import { BoardPage } from "../board/BoardPage";
import { MarketingBoardPage } from "../board/MarketingBoardPage";
import { MarketingResearchBoardPage } from "../board/MarketingResearchBoardPage";
import { DesignSystemBoardPage } from "../board/DesignSystemBoardPage";
import { YouTubeBoardPage } from "../board/YouTubeBoardPage";
import { YouTubeLabPage } from "../youtube-lab/YouTubeLabPage";
import { FalconBoardPage } from "../board/FalconBoardPage";
import { AirDuctBoardPage } from "../board/AirDuctBoardPage";
import { Everything420BoardPage } from "../board/Everything420BoardPage";
import { OutformBoardPage } from "../board/OutformBoardPage";
import { OutformConceptThreePage } from "../board/atlas/OutformConceptThreePage";
import { StartupBoardPage } from "../board/StartupBoardPage";
import { KnowledgeBoardPage } from "../board/KnowledgeBoardPage";
import { ElHaDegelBoardPage } from "../board/ElHaDegelBoardPage";
import { LironBoardPage } from "../board/LironBoardPage";
import { MaxSecuritySystemBoard } from "../board/MaxSecuritySystemBoard";
import { EladEngineeringBoardPage } from "../board/EladEngineeringBoardPage";
import { BoilingOffersBoardPage } from "../board/BoilingOffersBoardPage";
import { OpenSourceBoardPage } from "../board/OpenSourceBoardPage";
import { CommandBoardPage } from "../board/CommandBoardPage";
import { FlameKingManagerPage } from "../flameking-manager/FlameKingManagerPage";
import { FlameKingManagerDetailPage } from "../flameking-manager/FlameKingManagerDetailPage";
import { ProjectsListPage } from "../projects-dashboard/ProjectsListPage";
import { ProjectDetailPage } from "../projects-dashboard/ProjectDetailPage";
import { TicketsKanbanPage } from "../tickets/TicketsKanbanPage";
import { TicketDetailPage } from "../tickets/TicketDetailPage";

// Top-level route map. Tickets (DEM-18) + Goals were removed from the dashboard
// UI/nav. Projects are BACK (2026-07-07) as the memory-per-project surface: a
// projects table → detail page, each project's summary owned by its agent.
//
// Top-level app. AuthKitProvider + AuthGate in App.tsx gate this whole tree,
// so every route here assumes an authenticated user.
export const CRM = () => (
  <Layout>
    <Routes>
      {/* Root → Home (the curated landing page with the 3 deck cards). */}
      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route path="/home" element={<HomePage />} />

      {/* Meetings (Structured) — live status + transcript. */}
      <Route path="/meetings" element={<MeetingsPage />} />
      <Route path="/meetings/:id" element={<MeetingDetailPage />} />

      {/* Projects (Structured) — the memory-per-project surface (2026-07-07):
          a projects table + per-project detail; each project's summary is
          owned + written by its agent. Goals stay retired → home. */}
      <Route path="/goals" element={<Navigate to="/home" replace />} />
      <Route path="/goals-list" element={<Navigate to="/home" replace />} />
      <Route path="/goals/:slug" element={<Navigate to="/home" replace />} />
      <Route path="/projects" element={<Navigate to="/projects-list" replace />} />
      <Route path="/projects-list" element={<ProjectsListPage />} />
      <Route path="/projects/:slug" element={<ProjectDetailPage />} />
      <Route path="/projects-board" element={<Navigate to="/projects-list" replace />} />

      {/* Tickets — RESTORED 2026-08-11 (Erez). Retired in DEM-18 when work
          tracking moved to Paperclip, but the table, the API and an 824-line
          drag-and-drop Kanban were all still here and still correct; only the
          route had been pointed at /home. It comes back as the action-item
          board for EVERY project — one component, filtered by ?project=<slug>,
          which is what lets a project board embed its own slice of it. */}
      <Route path="/tickets" element={<TicketsKanbanPage />} />
      <Route path="/tickets/:slug" element={<TicketDetailPage />} />

      {/* Board (Structured) — infinite zoomable thinking canvas (React Flow). */}
      <Route path="/board" element={<BoardPage />} />
      <Route path="/marketing-board" element={<MarketingBoardPage />} />
      <Route path="/marketing-research" element={<MarketingResearchBoardPage />} />
      <Route path="/design-system-board" element={<DesignSystemBoardPage />} />
      <Route path="/youtube-board" element={<YouTubeBoardPage />} />
      <Route path="/youtube-lab" element={<YouTubeLabPage />} />
      <Route path="/falcon-board" element={<FalconBoardPage />} />
      <Route path="/air-duct-board" element={<AirDuctBoardPage />} />
      <Route path="/everything-420-board" element={<Everything420BoardPage />} />
      <Route path="/outform-board" element={<OutformBoardPage />} />
      {/* Standalone, deliberately outside the React Flow board — see the file
          header for why. Added 24 Aug 2026. */}
      <Route path="/outform-concept3" element={<OutformConceptThreePage />} />
      <Route path="/startup-board" element={<StartupBoardPage />} />
      <Route path="/knowledge-board" element={<KnowledgeBoardPage />} />
      <Route path="/elhadegel-board" element={<ElHaDegelBoardPage />} />
      <Route path="/liron-board" element={<LironBoardPage />} />
      <Route path="/max-security-board" element={<MaxSecuritySystemBoard />} />
      <Route path="/elad-engineering-board" element={<EladEngineeringBoardPage />} />
      <Route path="/boiling-offers-board" element={<BoilingOffersBoardPage />} />
      <Route path="/open-source-board" element={<OpenSourceBoardPage />} />

      {/* Agents (Structured). */}
      <Route path="/agents" element={<AgentsPage />} />
      <Route path="/operator-b" element={<Navigate to="/agents" replace />} />
      <Route path="/operator-c" element={<Navigate to="/agents" replace />} />

      {/* Skills (Knowledge — Classic detail). */}
      <Route path="/skills" element={<SkillsPage />} />
      <Route path="/command" element={<CommandBoardPage />} />
      <Route path="/skills/:slug" element={<SkillDetailPage />} />

      {/* Memory (Structured). */}
      <Route path="/memory" element={<MemoryPage />} />

      {/* Flame King Manager (Structured) — client-health board fed by the
          Flame King Manager agent on our Paperclip. */}
      <Route path="/flameking-manager" element={<FlameKingManagerPage />} />
      <Route path="/flameking-manager/:id" element={<FlameKingManagerDetailPage />} />

      {/* Users (Structured detail per tenant deployment). */}
      <Route path="/users" element={<UsersPage />} />
      <Route path="/users/:persona" element={<UserDetailPage />} />

      {/* Generic Knowledge renderers. */}
      <Route path="/kb-doc/*" element={<KbBlueprintPage />} />
      <Route path="/pitch-doc/*" element={<PitchDocBlueprintPage />} />

      {/* Knowledge Base index. */}
      <Route path="/knowledge-base" element={<KnowledgeBaseListPage />} />

      {/* Knowledge Feed (Structured) — youtube_watch table + per-video detail. */}
      <Route path="/knowledge-feed" element={<KnowledgeFeedListPage />} />
      <Route path="/knowledge-feed/:videoId" element={<KnowledgeFeedDetailPage />} />

      {/* Content Swipe File (Structured) — marketing swipe gallery. */}
      <Route path="/content-swipe-file" element={<ContentSwipeFilePage />} />
      {/* Content Plan (Structured) — content roadmap board by category/funnel. */}
      <Route path="/content-plan" element={<ContentPlanPage />} />
      {/* Content Story Map (Structured). */}
      <Route path="/content-map" element={<ContentMapPage />} />

      {/* MJOS-132 — Avatars Lab: live demos of OSS 3D avatar libraries. */}
      <Route path="/avatars" element={<AvatarsPage />} />

      {/* 3D Showcase — five real WebGL (Three.js / r3f) demos. */}
      <Route path="/threejs-showcase" element={<ThreeShowcasePage />} />
      {/* 3D Knowledge board PROTOTYPES — same KB data, two engines. */}

      {/* Settings (sidebar account dropdown). */}
      <Route path="/settings" element={<SettingsPage />} />

      {/* Anything else falls through to home. */}
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  </Layout>
);
